// The one request behind the air contact page: POST https://api.trackingmcp.com/v1/contact-sales.
//
// WHY THIS IS ITS OWN MODULE (SCRUM-61): from 2026-09-17 the shared intake refuses any post that does
// not carry a Cloudflare Turnstile token, answering 403 CHALLENGE_FAILED. This page never sent one, so
// every submit on aircargomcp.com and airratesmcp.com showed the error state and no lead was saved for
// over a month. The token now rides in the `x-turnstile-token` header, exactly as trackingmcp.com's own
// Contact sales modal sends it, and the request and its answers live here so they can be tested without
// a browser.
//
// What the intake answers, and what each answer means for the form (trackingmcp apps/api
// src/routes/contact-sales.ts + src/lib/leads/contact-sales-schema.ts):
//   - 200 { ok: true }                         the lead is saved.
//   - 400 VALIDATION_ERROR | MESSAGE_TOO_LONG  `error.fields` names each bad field; shown inline.
//   - 403 CHALLENGE_FAILED                     the token was missing, expired or refused. One retry with
//                                              a fresh token, since a token is single-use and a slow
//                                              widget can hand back one that has already lapsed.
//   - any other 4xx                            the API's own sentence is shown as it is.
//   - 5xx or no answer at all                  a generic failure with the sales mailbox as the way out.

import { AUTH_TURNSTILE_SITEKEY, solveTurnstile } from './turnstile'

export const CONTACT_ENDPOINT = 'https://api.trackingmcp.com/v1/contact-sales'

/** The intake's own caps (CONTACT_SALES_LIMITS on the server). The inputs carry the same maxLength. */
export const CONTACT_LIMITS = {
  name: 120,
  company: 160,
  email: 254,
  message: 10_000,
} as const

export type ContactField = 'name' | 'company' | 'email' | 'message'

export interface ContactPayload {
  name: string
  company: string
  email: string
  message: string
  source: string
}

export type ContactResult =
  | { ok: true }
  | {
      ok: false
      /** What went wrong, which decides how the form presents it. */
      kind: 'challenge' | 'validation' | 'refused' | 'unavailable'
      /** A sentence fit to show on its own. */
      message: string
      /** Inline messages for the fields the server named. Empty unless kind is 'validation'. */
      fields: Partial<Record<ContactField, string>>
    }

export interface ContactDeps {
  fetch: typeof fetch
  /** Resolve a Turnstile token, or null when the challenge could not produce one. */
  solve: () => Promise<string | null>
  /** False when this build has no site key: the post then goes without a token and the server decides. */
  challengeConfigured: boolean
}

const defaultDeps = (): ContactDeps => ({
  fetch: (...args) => fetch(...args),
  solve: () => solveTurnstile(AUTH_TURNSTILE_SITEKEY),
  challengeConfigured: Boolean(AUTH_TURNSTILE_SITEKEY),
})

export const CHALLENGE_MESSAGE =
  'The security check could not be completed, so your request was not sent. Your details are still in the form, so try again in a moment.'

export const UNAVAILABLE_MESSAGE =
  'Something went wrong sending that, so your request was not sent. Your details are still in the form.'

const FIELDS: readonly ContactField[] = ['name', 'company', 'email', 'message']

interface ApiError {
  code?: unknown
  message?: unknown
  fields?: unknown
}

function pickFields(raw: unknown): Partial<Record<ContactField, string>> {
  const out: Partial<Record<ContactField, string>> = {}
  if (!raw || typeof raw !== 'object') return out
  for (const f of FIELDS) {
    const v = (raw as Record<string, unknown>)[f]
    if (typeof v === 'string' && v.trim()) out[f] = v
  }
  return out
}

/** Post one contact request. Never throws: every outcome comes back as a ContactResult. */
export async function submitContactSales(
  payload: ContactPayload,
  deps: ContactDeps = defaultDeps(),
): Promise<ContactResult> {
  const body = JSON.stringify({
    name: payload.name.trim(),
    email: payload.email.trim(),
    company: payload.company.trim(),
    message: payload.message.trim(),
    topic: 'api',
    source: payload.source,
  })

  // At most two posts: the first, and one more with a fresh token if the first token was refused.
  for (let attempt = 0; attempt < 2; attempt++) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }
    if (deps.challengeConfigured) {
      const token = await deps.solve()
      // No token means the intake will refuse the post. Say so instead of sending a request we know
      // will bounce, and leave everything the person typed where it is.
      if (!token) return { ok: false, kind: 'challenge', message: CHALLENGE_MESSAGE, fields: {} }
      headers['x-turnstile-token'] = token
    }

    let res: Response
    try {
      res = await deps.fetch(CONTACT_ENDPOINT, { method: 'POST', headers, body })
    } catch {
      return { ok: false, kind: 'unavailable', message: UNAVAILABLE_MESSAGE, fields: {} }
    }
    const json = (await res.json().catch(() => null)) as {
      ok?: unknown
      error?: ApiError
    } | null
    if (res.ok && json?.ok) return { ok: true }

    const err = json?.error ?? {}
    const code = typeof err.code === 'string' ? err.code : ''
    const apiMessage = typeof err.message === 'string' && err.message.trim() ? err.message : ''

    if (res.status === 403 && code === 'CHALLENGE_FAILED') {
      if (attempt === 0) continue
      return { ok: false, kind: 'challenge', message: CHALLENGE_MESSAGE, fields: {} }
    }
    if (res.status === 400 && (code === 'VALIDATION_ERROR' || code === 'MESSAGE_TOO_LONG')) {
      const fields = pickFields(err.fields)
      if (code === 'MESSAGE_TOO_LONG' && !fields.message && apiMessage) fields.message = apiMessage
      return {
        ok: false,
        kind: 'validation',
        message: apiMessage || 'Some of the details need a fix before we can send them.',
        fields,
      }
    }
    if (res.status >= 400 && res.status < 500) {
      return {
        ok: false,
        kind: 'refused',
        message: apiMessage || UNAVAILABLE_MESSAGE,
        fields: {},
      }
    }
    return { ok: false, kind: 'unavailable', message: UNAVAILABLE_MESSAGE, fields: {} }
  }
  // Unreachable: the second pass always returns.
  return { ok: false, kind: 'challenge', message: CHALLENGE_MESSAGE, fields: {} }
}
